import { z } from 'zod'
import { shapeSchema } from './shapes.js'

export const GAME_MODES = ['click_race', 'count_sequential', 'count_scatter'] as const
export const gameModeSchema = z.enum(GAME_MODES)
export type GameMode = z.infer<typeof gameModeSchema>

export const MODE_META: Record<GameMode, { name: string; tagline: string }> = {
  click_race: {
    name: 'Click Race',
    tagline: 'Most clicks before the clock runs out.',
  },
  count_sequential: {
    name: 'Sequential Count',
    tagline: 'One shape, flashing in and out. Count every single one.',
  },
  count_scatter: {
    name: 'Scatter Count',
    tagline: 'The screen keeps changing. Keep a running total of one shape.',
  },
}

/** Click Race runs for the full window the host picks. */
export const CLICK_RACE_DURATIONS = [30, 60, 90] as const
export const clickRaceDurationSchema = z.union([
  z.literal(30),
  z.literal(60),
  z.literal(90),
])
export type ClickRaceDuration = z.infer<typeof clickRaceDurationSchema>

/**
 * For the counting modes the host's pick is the *round one* display window.
 * Later rounds tighten it — see `game-core/ramp.ts`.
 */
export const COUNT_DURATIONS = [10, 20, 30] as const
export const countDurationSchema = z.union([
  z.literal(10),
  z.literal(20),
  z.literal(30),
])
export type CountDuration = z.infer<typeof countDurationSchema>

/** Every player gets exactly 5 seconds to enter their answer. Non-negotiable. */
export const ANSWER_WINDOW_MS = 5_000

/**
 * Which coins a match is played for.
 *
 * `practice` - practice coins, against the bot. Free to top up, never
 *              redeemable, so a mode can be learned without anything at risk.
 * `prize`    - prize coins, against real people. Backed one-for-one by cents,
 *              so the pot is real money and the winner can redeem it.
 *
 * The purse decides both the currency and the opponent. There is deliberately
 * no combination that puts a bot in a prize match: a house-run opponent playing
 * for a player's money is a different product with a different legal footing.
 */
export const PURSES = ['practice', 'prize'] as const
export const purseSchema = z.enum(PURSES)
export type Purse = z.infer<typeof purseSchema>

/**
 * Stake tiers. Every player antes the same amount.
 *
 * One tier list for both purses, because one prize coin is one cent: the same
 * `500` means 500 practice coins or $5.00, and the ladder feels identical
 * whichever purse a player is in.
 */
export const STAKE_TIERS = [50, 100, 250, 500, 1000] as const
/** 0 opens an unstaked room, which never touches the ledger. */
export const stakeSchema = z.union([
  z.literal(0),
  z.literal(50),
  z.literal(100),
  z.literal(250),
  z.literal(500),
  z.literal(1000),
])
export type Stake = z.infer<typeof stakeSchema>

export const matchConfigSchema = z
  .object({
    mode: gameModeSchema,
    /** 30/60/90 for click_race, 10/20/30 for the counting modes. */
    duration: z.number().int().positive(),
    stake: stakeSchema,
    maxPlayers: z.number().int().min(2).max(4),
    purse: purseSchema.default('practice'),
  })
  .superRefine((cfg, ctx) => {
    const allowed: readonly number[] =
      cfg.mode === 'click_race' ? CLICK_RACE_DURATIONS : COUNT_DURATIONS
    if (!allowed.includes(cfg.duration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: `Duration for ${cfg.mode} must be one of ${allowed.join(', ')}s`,
      })
    }

    // A prize match is real money, so it always has a real ante. An unstaked
    // prize room would pay a winner nothing while still looking like cash play.
    if (cfg.purse === 'prize' && cfg.stake === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stake'],
        message: 'A prize match needs a real ante',
      })
    }
  })
export type MatchConfig = z.infer<typeof matchConfigSchema>

/** True for a room that moves no coins at all: a free look at a mode. */
export function isUnstaked(config: Pick<MatchConfig, 'stake'>): boolean {
  return config.stake === 0
}

// ---------------------------------------------------------------------------
// Round content
//
// Positions and sizes are normalised to 0..1 against the play surface so the
// same payload renders correctly on a 375px phone and a 2560px monitor without
// the server knowing anything about viewports.
// ---------------------------------------------------------------------------

export const sequentialItemSchema = z.object({
  id: z.string(),
  shape: shapeSchema,
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  size: z.number().min(0).max(1),
  rotation: z.number(),
  /** ms from round start */
  appearAt: z.number().int().min(0),
  /** ms from round start */
  disappearAt: z.number().int().min(0),
})
export type SequentialItem = z.infer<typeof sequentialItemSchema>

export const scatterItemSchema = z.object({
  id: z.string(),
  shape: shapeSchema,
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  size: z.number().min(0).max(1),
  rotation: z.number(),
})
export type ScatterItem = z.infer<typeof scatterItemSchema>

export const sequentialRoundSchema = z.object({
  kind: z.literal('count_sequential'),
  roundIndex: z.number().int().min(1),
  displayMs: z.number().int().positive(),
  targetShape: shapeSchema,
  items: z.array(sequentialItemSchema),
})
export type SequentialRound = z.infer<typeof sequentialRoundSchema>

/**
 * One screenful in Scatter mode. The field is replaced wholesale every
 * `frameMs`, and the player is counting the target across *all* of them.
 */
export const scatterFrameSchema = z.object({
  index: z.number().int().min(0),
  /** ms from round start */
  startAt: z.number().int().min(0),
  /** ms from round start */
  endAt: z.number().int().min(0),
  items: z.array(scatterItemSchema),
})
export type ScatterFrame = z.infer<typeof scatterFrameSchema>

export const scatterRoundSchema = z.object({
  kind: z.literal('count_scatter'),
  roundIndex: z.number().int().min(1),
  displayMs: z.number().int().positive(),
  targetShape: shapeSchema,
  /** How long each screenful stays up before it is replaced. */
  frameMs: z.number().int().positive(),
  frames: z.array(scatterFrameSchema),
})
export type ScatterRound = z.infer<typeof scatterRoundSchema>

/**
 * Click Race has no shapes, but it still travels on the same `round:display`
 * event. Giving it its own variant is cleaner than sending an empty counting
 * round and asking every consumer to infer the mode from an empty array.
 */
export const clickRaceRoundSchema = z.object({
  kind: z.literal('click_race'),
  roundIndex: z.number().int().min(1),
  displayMs: z.number().int().positive(),
})
export type ClickRaceRound = z.infer<typeof clickRaceRoundSchema>

export const countRoundSchema = z.discriminatedUnion('kind', [
  sequentialRoundSchema,
  scatterRoundSchema,
])
export type CountRound = z.infer<typeof countRoundSchema>

export const roundSchema = z.discriminatedUnion('kind', [
  sequentialRoundSchema,
  scatterRoundSchema,
  clickRaceRoundSchema,
])
export type Round = z.infer<typeof roundSchema>
